/**
 * Global “work in progress” feedback: wait cursor + top strip + aria-busy on <html>.
 *
 * - Ref-counted so nested/overlapping async work and parallel jQuery requests behave correctly.
 * - Turbolinks full-page fetches: turbolinks:request-start / request-end
 * - jQuery: ajaxSend / ajaxComplete (per request)
 * - Manual/async: import { withBusy, beginBusy, endBusy } and wrap fetch() or heavy sync work
 */

let depth = 0

function syncDom() {
  const el = document.documentElement
  if (depth > 0) {
    el.classList.add("app-is-busy")
    el.setAttribute("aria-busy", "true")
  } else {
    el.classList.remove("app-is-busy")
    el.removeAttribute("aria-busy")
  }
}

export function beginBusy() {
  depth += 1
  if (depth === 1) syncDom()
}

export function endBusy() {
  if (depth <= 0) return
  depth -= 1
  if (depth === 0) syncDom()
}

/** Clears busy state (e.g. Turbolinks snapshot / navigation abort). */
export function resetBusy() {
  depth = 0
  syncDom()
}

/**
 * @template T
 * @param {() => T | Promise<T>} fn
 * @returns {Promise<T>}
 */
export async function withBusy(fn) {
  beginBusy()
  try {
    return await fn()
  } finally {
    endBusy()
  }
}

let listenersBound = false

export function initAppBusy() {
  if (listenersBound) return
  listenersBound = true

  document.addEventListener("turbolinks:request-start", () => {
    beginBusy()
  })
  document.addEventListener("turbolinks:request-end", () => {
    endBusy()
  })
  document.addEventListener("turbolinks:before-cache", () => {
    resetBusy()
  })

  const $ = window.jQuery
  if ($) {
    $(document).on("ajaxSend.appBusy", function () {
      beginBusy()
    })
    $(document).on("ajaxComplete.appBusy", function () {
      endBusy()
    })
  }
}
